// frontend-react/src/components/RuleManagement/RuleAIOptimizeTab.tsx
import React, { useState } from 'react';
import {
  Button,
  Input,
  Checkbox,
  Typography,
  Alert,
  Spin,
  Empty,
  message,
  Space,
} from 'antd';
import { CheckOutlined, RobotOutlined, DownOutlined } from '@ant-design/icons';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
  generateAiRuleSuggestions,
  applyRuleOptimizeSuggestions,
} from '../../services/ruleService';
import type { RuleOptimizeSuggestion } from '../../types/index';

const { Text, Paragraph } = Typography;

// ── 配色常數（與編輯紀錄頁保持語意一致）──
// 原始規則：中性灰
const BEFORE_BG = '#F5F7FA';
const BEFORE_BORDER = '#9CA3AF';
const BEFORE_LABEL = '#6B7280';
// 建議修改後：品牌青藍（編輯 = 藍）
const AFTER_BG = '#E6F4F8';
const AFTER_BORDER = '#1E88A8';
const AFTER_LABEL = '#1E88A8';
// AI 分析：橄欖綠（與編輯紀錄刪除標籤一致）
const AI_ANALYSIS_BG = '#F0F7F2';
const AI_ANALYSIS_BORDER = '#6B7B5A';
const AI_ANALYSIS_LABEL = '#4A5E3A';

interface Props {
  ruleSetId: number;
  onApplied?: () => void;
}

const RuleAIOptimizeTab: React.FC<Props> = ({ ruleSetId, onApplied }) => {
  const [userContext, setUserContext] = useState('');

  const SUGGESTIONS = ['請分析條款修改的原因', '哪些規則誤報率過高？', '規則是否需要縮小適用範圍？'];
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState<RuleOptimizeSuggestion[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [generated, setGenerated] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(new Set());

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    setSuggestions([]);
    setSelectedIndexes(new Set());
    setGenerated(false);

    try {
      const result = await generateAiRuleSuggestions(ruleSetId, userContext);
      setSuggestions(result);
      setSelectedIndexes(new Set(result.map((_, i) => i)));
      setExpandedIndexes(new Set());
      setGenerated(true);
    } catch (err: any) {
      setGenError(err?.message ?? 'AI 優化建議產生失敗，請稍後再試');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelect = (idx: number, e: React.MouseEvent | CheckboxChangeEvent) => {
    e.stopPropagation();
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleApply = async () => {
    const toApply = suggestions.filter((_, i) => selectedIndexes.has(i));
    if (toApply.length === 0) {
      message.warning('請至少選擇一條建議');
      return;
    }

    setApplying(true);
    try {
      const result = await applyRuleOptimizeSuggestions(ruleSetId, toApply);
      message.success(result.message || `已套用 ${result.applied_count} 條規則優化建議`);
      message.info('提醒：可至「編輯紀錄」清空已套用的編輯紀錄', 5);
      const appliedIndexes = new Set(toApply.map(s => s.rule_index));
      setSuggestions(prev => prev.filter(s => !appliedIndexes.has(s.rule_index)));
      setSelectedIndexes(new Set());
      onApplied?.();
    } catch (err: any) {
      message.error(err?.message ?? '套用建議失敗');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      {/* 補充說明 + 開始按鈕（同一行）*/}
      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        <Input
          placeholder='輸入分析方向，或點選下方建議...'
          value={userContext}
          onChange={e => setUserContext(e.target.value)}
          disabled={generating}
          maxLength={500}
          style={{ flex: 1 }}
        />
        <Button
          type='primary'
          loading={generating}
          onClick={handleGenerate}
        >
          開始
        </Button>
      </div>

      {/* 建議標籤 */}
      {!generating && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {SUGGESTIONS.map(s => (
            <span
              key={s}
              onClick={() => setUserContext(s)}
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 12,
                fontSize: 12,
                cursor: 'pointer',
                background: userContext === s ? '#E6F4F8' : '#FAFAFA',
                border: `1px solid ${userContext === s ? '#1E88A8' : '#D1D5DB'}`,
                color: userContext === s ? '#1E88A8' : '#6B7280',
                transition: 'all 0.15s',
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {genError && (
        <Alert
          type='error'
          message={genError}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setGenError(null)}
        />
      )}

      {generating && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin tip='AI 正在分析編輯數據，產生規則優化建議...' />
        </div>
      )}

      {generated && !generating && suggestions.length === 0 && !genError && (
        <Empty
          description='AI 分析後認為此規則集目前無需優化，或尚無足夠的編輯紀錄供分析'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* 建議列表 */}
      {suggestions.length > 0 && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>
              共 {suggestions.length} 條優化建議（已選 {selectedIndexes.size} 條）
            </Text>
            <Space>
              <Button
                size='small'
                onClick={() => setSelectedIndexes(new Set(suggestions.map((_, i) => i)))}
              >
                全選
              </Button>
              <Button size='small' onClick={() => setSelectedIndexes(new Set())}>
                清除選取
              </Button>
            </Space>
          </div>

          <div style={{ marginBottom: 16 }}>
            {suggestions.map((s, idx) => {
              const isExpanded = expandedIndexes.has(idx);
              return (
                <div key={s.rule_index}>
                  {/* 標題列 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 4px',
                      borderBottom: `1px solid ${isExpanded ? 'transparent' : '#F0F0F0'}`,
                    }}
                  >
                    <Checkbox
                      checked={selectedIndexes.has(idx)}
                      onChange={e => toggleSelect(idx, e)}
                      onClick={e => e.stopPropagation()}
                    />
                    {/* 點擊規則名稱展開／收合 */}
                    <div
                      role='button'
                      tabIndex={0}
                      style={{ flex: 1, cursor: 'pointer' }}
                      onClick={e => toggleExpand(idx, e)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') toggleExpand(idx, e as any);
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        規則 {s.rule_index + 1}｜{s.rule_name}
                      </Text>
                    </div>
                    <DownOutlined
                      onClick={e => toggleExpand(idx, e)}
                      style={{
                        fontSize: 11,
                        color: '#9CA3AF',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.2s',
                        padding: '4px 6px',
                        cursor: 'pointer',
                      }}
                    />
                  </div>

                  {/* 展開後的詳細內容 */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '12px 4px 16px',
                        borderBottom: '1px solid #F0F0F0',
                      }}
                    >
                      {/* AI 分析 */}
                      <div
                        style={{
                          background: AI_ANALYSIS_BG,
                          borderLeft: `3px solid ${AI_ANALYSIS_BORDER}`,
                          borderRadius: '0 4px 4px 0',
                          padding: '8px 12px',
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: AI_ANALYSIS_LABEL,
                            fontWeight: 600,
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <RobotOutlined />
                          AI 分析
                        </div>
                        <Text style={{ color: '#374151', fontSize: 13 }}>{s.reason}</Text>
                      </div>

                      {/* 原始規則 / 建議修改後 並排 */}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: BEFORE_LABEL,
                              fontWeight: 600,
                              marginBottom: 4,
                            }}
                          >
                            原始規則
                          </div>
                          <Paragraph
                            style={{
                              background: BEFORE_BG,
                              borderLeft: `3px solid ${BEFORE_BORDER}`,
                              borderRadius: '0 4px 4px 0',
                              padding: '6px 10px',
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              marginBottom: 0,
                              color: '#374151',
                            }}
                          >
                            {s.original_content}
                          </Paragraph>
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: AFTER_LABEL,
                              fontWeight: 600,
                              marginBottom: 4,
                            }}
                          >
                            建議修改後
                          </div>
                          <Paragraph
                            style={{
                              background: AFTER_BG,
                              borderLeft: `3px solid ${AFTER_BORDER}`,
                              borderRadius: '0 4px 4px 0',
                              padding: '6px 10px',
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              marginBottom: 0,
                              color: '#374151',
                            }}
                          >
                            {s.suggested_content}
                          </Paragraph>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 套用按鈕 */}
          <div style={{ textAlign: 'right' }}>
            <Button
              type='primary'
              icon={<CheckOutlined />}
              loading={applying}
              disabled={selectedIndexes.size === 0}
              onClick={handleApply}
            >
              套用規則
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleAIOptimizeTab;
